import React, { useEffect, useState } from "react";
import { FaUtensils } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AddRecipeFormPanel from "../components/AddRecipeFormPanel";
import EditRecipeFormPanel from "../components/EditRecipeFormPanel";
import { db } from "../firebase";
import { addDoc, collection, deleteDoc, doc, updateDoc, onSnapshot, orderBy, query, serverTimestamp } from "firebase/firestore";

const initialRecipes = [];

const categories = [
	'Breakfasts & Snacks',
	'Lunches & Dinners',
	'Desserts & Sweets',
	'Soups & Stews',
];

const getLocalizedCategory = (category, language) => {
	const categoryMap = {
		'Breakfasts & Snacks': { English: 'Breakfasts & Snacks', Spanish: 'Desayunos y Meriendas' },
		'Lunches & Dinners': { English: 'Lunches & Dinners', Spanish: 'Comidas y Cenas' },
		'Desserts & Sweets': { English: 'Desserts & Sweets', Spanish: 'Postres y Dulces' },
		'Soups & Stews': { English: 'Soups & Stews', Spanish: 'Sopas, Guisos y Caldos' },
	};
	return categoryMap[category]?.[language || 'English'] || category;
};

const RecipeManagement = () => {
	const [recipes, setRecipes] = useState(initialRecipes);
	const [currentPage, setCurrentPage] = useState(1);
	const recipesPerPage = 5;
	const totalPages = Math.ceil(recipes.length / recipesPerPage);
	const paginatedRecipes = recipes.slice((currentPage - 1) * recipesPerPage, currentPage * recipesPerPage);
	const [sidePanelOpen, setSidePanelOpen] = useState(false);
	const [form, setForm] = useState({
		image: "",
		name: "",
		category: "",
		language: "English",
		htmlContent: "",
	});
	const [imagePreview, setImagePreview] = useState("");
	const [error, setError] = useState("");
	const [addPanelOpen, setAddPanelOpen] = useState(false);
	const [editPanelOpen, setEditPanelOpen] = useState(false);
	const [selectedRecipe, setSelectedRecipe] = useState(null);
	const navigate = useNavigate();

	useEffect(() => {
		const q = query(collection(db, "admin", "recipes", "items"), orderBy("createdAt", "desc"));
		const unsub = onSnapshot(q, (snap) => {
			const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
			setRecipes(list);
		});
		return () => unsub();
	}, []);

	const handleFormChange = (e) => {
		setForm({ ...form, [e.target.name]: e.target.value });
		setError("");
	};

	const handleAddRecipe = async (recipeData, resetForm) => {
		if (!recipeData.name || !recipeData.category || !recipeData.image || !recipeData.htmlContent) {
			setError("All fields (Title, Category, Image Link, and HTML) are required.");
			return;
		}
		try {
			await addDoc(collection(db, "admin", "recipes", "items"), {
				...recipeData,
				createdAt: serverTimestamp(),
			});
			setError("");
			resetForm(); // Clear the form in the AddRecipeFormPanel
			setAddPanelOpen(false);
		} catch (err) {
			console.error("Error adding recipe:", err);
			setError("Failed to add recipe. Please try again.");
		}
	};

	const handleEditClick = (recipe, e) => {
		e.stopPropagation();
		setSelectedRecipe(recipe);
		setEditPanelOpen(true);
		setError("");
	};

	const handleEditRecipe = async (recipeData) => {
		if (!recipeData.name || !recipeData.category || !recipeData.image || !recipeData.htmlContent) {
			setError("All fields are required.");
			return;
		}
		try {
			const recipeRef = doc(db, "admin", "recipes", "items", recipeData.id);
			await updateDoc(recipeRef, {
				...recipeData,
				updatedAt: serverTimestamp(),
			});
			setEditPanelOpen(false);
			setSelectedRecipe(null);
			setError("");
		} catch (err) {
			console.error("Error updating recipe:", err);
			setError("Failed to update recipe.");
		}
	};

	const handleDelete = async (id, e) => {
		e.stopPropagation();
		await deleteDoc(doc(db, "admin", "recipes", "items", id));
	};

	return (
		<div className="min-h-screen flex flex-col py-4 px-2 sm:px-4 md:px-8 relative">
			{/* Side Panel */}
			<AddRecipeFormPanel
				open={addPanelOpen}
				onClose={() => { setAddPanelOpen(false); setError(""); }}
				onSubmit={handleAddRecipe}
				categories={categories}
				error={error}
			/>
			<EditRecipeFormPanel
				open={editPanelOpen}
				onClose={() => { setEditPanelOpen(false); setSelectedRecipe(null); setError(""); }}
				onSubmit={handleEditRecipe}
				categories={categories}
				error={error}
				initialData={selectedRecipe}
			/>
			<div className="w-full max-w-full mx-auto">
				<div className="mb-4 flex flex-col sm:flex-row items-stretch sm:items-start justify-between gap-2 px-0 sm:px-2">
					<input
						type="text"
						placeholder="Search by recipe name or category.."
						className="w-full sm:max-w-md px-4 py-2 rounded-xl border border-orange focus:outline-none focus:ring-2 focus:ring-orange2 bg-white text-gray-700 shadow text-xs sm:text-sm md:text-base"
					/>
					<button
						className="bg-orange2 py-2 px-4 rounded-xl text-white w-full sm:w-auto text-xs sm:text-sm md:text-base font-bold transition-colors hover:bg-orange focus:ring-2 focus:ring-orange2 focus:outline-none shadow"
						onClick={() => setAddPanelOpen(true)}
					>
						<FaUtensils className="inline-block mr-2 mb-0.5" /> Add New Recipe
					</button>
				</div>
				<div className="bg-white rounded-3xl shadow-xl w-full">
					<div className="overflow-x-auto scrollbar-thin scrollbar-thumb-orange/30 scrollbar-track-orange/10 rounded-3xl">
						<table className="w-full text-xs sm:text-sm min-w-[700px] md:min-w-[900px] text-center">
							<thead>
								<tr className="bg-gradient-to-r from-orange to-yellow text-white">
									<th className="py-3 px-2 font-bold min-w-[32px] w-8"></th>
									<th className="py-3 px-2 font-bold min-w-[100px]">
										RECIPE IMAGE
									</th>
									<th className="py-3 px-2 font-bold min-w-[160px]">
										RECIPE NAME
									</th>
									<th className="py-3 px-2 font-bold min-w-[120px]">
										CATEGORY
									</th>
									<th className="py-3 px-2 font-bold min-w-[120px]">
										LANGUAGE
									</th>
									<th className="py-3 px-2 font-bold min-w-[120px]">
										ACTION
									</th>
								</tr>
							</thead>
							<tbody>
								{paginatedRecipes.map((recipe) => (
									<tr
										key={recipe.id}
										className="last:border-b-0 group bg-white border-b border-orange/10 cursor-pointer transition-colors hover:bg-gradient-to-r hover:from-orange/10 hover:to-yellow/10 hover:text-orange-900"
										onClick={() => navigate(`/recipe/${recipe.id}`)}
									>
										<td className="py-4 px-2 align-middle group-hover:bg-orange/10 transition-colors min-w-[32px] w-8">
											<input
												type="checkbox"
												className="w-4 h-4 accent-orange2"
											/>
										</td>
										<td className="py-4 px-2 align-middle group-hover:bg-orange/10 transition-colors min-w-[100px]">
											<img
												src={recipe.image}
												alt={recipe.name}
												className="w-16 h-16 rounded-xl object-cover border border-orange/20 shadow-sm mx-auto"
											/>
										</td>
										<td className="py-4 px-2 align-middle group-hover:bg-orange/10 transition-colors min-w-[160px] font-bold text-gray-900 text-xs sm:text-sm md:text-base break-words max-w-[160px] md:max-w-[220px] truncate">
											{recipe.name}
										</td>
										<td className="py-4 px-2 align-middle group-hover:bg-orange/10 transition-colors min-w-[120px]">
											<span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow/20 text-yellow">
												{getLocalizedCategory(recipe.category, recipe.language)}
											</span>
										</td>
										<td className="py-4 px-2 align-middle group-hover:bg-orange/10 transition-colors min-w-[120px]">
											<span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-600">
												{recipe.language || "English"}
											</span>
										</td>
										<td className="py-4 px-2 align-middle group-hover:bg-orange/10 transition-colors min-w-[120px]">
											<div className="flex gap-2 justify-center">
												<button className="px-3 py-1 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold flex items-center space-x-1 shadow-md border-0 hover:from-indigo-500 hover:to-blue-500 transition-all text-xs sm:text-sm md:text-base"
													onClick={(e) => handleEditClick(recipe, e)}
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														className="h-4 w-4 mr-1"
														fill="none"
														viewBox="0 0 24 24"
														strokeWidth="2"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
														/>
													</svg>
													Edit
												</button>
												<button className="px-3 py-1 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold flex items-center space-x-1 shadow-md border-0 hover:from-orange-500 hover:to-red-500 transition-all text-xs sm:text-sm md:text-base"
													onClick={(e) => handleDelete(recipe.id, e)}
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														className="h-4 w-4 mr-1"
														fill="none"
														viewBox="0 0 24 24"
														strokeWidth="2"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M6 19a2 2 0 002 2h8a2 2 0 002-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5m1 0v2h12V5m-6 4v6"
														/>
													</svg>
													Delete
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
			{/* Pagination */}
			<div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm sm:text-base text-white gap-2 px-2 w-full">
				<span className="text-xs sm:text-sm">
					Showing {recipes.length === 0 ? 0 : (currentPage - 1) * recipesPerPage + 1}
					to {Math.min(currentPage * recipesPerPage, recipes.length)} of {recipes.length} entries
				</span>
				<div className="flex items-center space-x-1 w-full sm:w-auto justify-center sm:justify-start">
					<button
						className="px-3 py-1 rounded-xl bg-gradient-to-r from-orange to-orange2 text-white font-bold text-xs sm:text-sm shadow-md border-0 hover:from-orange2 hover:to-orange transition-all"
						disabled={currentPage === 1}
						onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
					>
						Previous
					</button>
					{Array.from({ length: totalPages }, (_, i) => (
						<button
							key={i + 1}
							className={`px-3 py-1 rounded-xl ${currentPage === i + 1 ? 'bg-gradient-to-r from-orange to-yellow' : 'bg-gradient-to-r from-orange to-orange2 hover:from-orange2 hover:to-orange'} text-white font-bold text-xs sm:text-sm shadow-md border-0`}
							onClick={() => setCurrentPage(i + 1)}
						>
							{i + 1}
						</button>
					))}
					<button
						className="px-3 py-1 rounded-xl bg-gradient-to-r from-orange to-orange2 text-white font-bold text-xs sm:text-sm shadow-md border-0 hover:from-orange2 hover:to-orange transition-all"
						disabled={currentPage === totalPages || totalPages === 0}
						onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
					>
						Next
					</button>
				</div>
			</div>
		</div>
	);
};

export default RecipeManagement;
